// frontend/app/components/lessonPlans/present/PresentSurface.js
//
// The shared parts of a slide. The workspace's LessonBlockShell wraps
// everything in a bordered card, which is right for a scrolling document and
// wrong for a wall: thirty outlined boxes read as a form to fill in. Here a
// block is separated by space and type size instead, and a border is spent only
// where it means something — on a revealed answer, or on a note the room can
// see.
//
// Every text style comes in as `type` (see presentTheme.getPresentType) rather
// than being written into the StyleSheet, because the sizes depend on the
// viewport and a StyleSheet is created once.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

/** The step's kicker: phase name, "Predict first", "Discuss". Quiet by design. */
export const PresentEyebrow = ({ type, color, children }) => {
  if (!children) return null;
  return (
    <Text style={[styles.eyebrow, type.eyebrow, { color }]}>{children}</Text>
  );
};

/** The question the room is answering — the largest thing on any slide. */
export const PresentPrompt = ({ type, colors, children }) => {
  if (!children) return null;
  return (
    <Text style={[styles.prompt, type.prompt, { color: colors.textPrimary }]}>
      {children}
    </Text>
  );
};

export const PresentHeading = ({ type, colors, children }) => {
  if (!children) return null;
  return (
    <Text style={[styles.heading, type.stepTitle, { color: colors.textPrimary }]}>
      {children}
    </Text>
  );
};

export const PresentBody = ({ type, colors, color, children, style }) => {
  if (!children) return null;
  return (
    <Text
      style={[
        styles.body,
        type.body,
        { color: color || colors.textSecondary },
        style,
      ]}
    >
      {children}
    </Text>
  );
};

/**
 * A block occupies a lane rather than a card. Prose caps narrower than the
 * stage so a 40px line does not run 1900px wide and lose the reader between one
 * line and the next; the asset stage opts out with `wide`.
 */
export const PresentLane = ({ children, wide = false, style }) => (
  <View style={[styles.lane, wide ? styles.laneWide : null, style]}>{children}</View>
);

/**
 * The second beat: the answer key, once the teacher has taken answers. Bordered
 * and tinted because this is the one moment a slide should look different from
 * the moment before it — that change is the signal the room is watching for.
 */
export const PresentRevealPanel = ({ type, colors, label, children }) => (
  <View
    style={[
      styles.revealPanel,
      { backgroundColor: colors.correctBg, borderColor: colors.correctBorder },
    ]}
  >
    {label ? (
      <View style={styles.revealLabelRow}>
        <MaterialCommunityIcons
          name="check-circle-outline"
          size={Math.round(type.caption.fontSize * 1.1)}
          color={colors.success}
        />
        <Text style={[styles.revealLabel, type.eyebrow, { color: colors.success }]}>
          {label}
        </Text>
      </View>
    ) : null}
    {children}
  </View>
);

/**
 * The control that turns the question into its answer.
 *
 * Rendered only where `revealFor` found something to show — a Reveal button
 * that reveals nothing teaches the teacher to stop trusting the button.
 */
export const RevealButton = ({ type, colors, label, onPress, accent }) => {
  const tone = accent || colors.accentPrimary;
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={[
        styles.revealButton,
        {
          borderColor: tone,
          backgroundColor: colors.accentGlow,
          paddingVertical: Math.round(type.caption.fontSize * 0.7),
          paddingHorizontal: Math.round(type.caption.fontSize * 1.3),
        },
      ]}
    >
      <MaterialCommunityIcons
        name="eye-outline"
        size={Math.round(type.caption.fontSize * 1.2)}
        color={tone}
      />
      <Text style={[styles.revealButtonText, type.caption, { color: tone }]}>
        {label}
      </Text>
    </TouchableOpacity>
  );
};

/**
 * A small control for the chrome bar.
 *
 * `active` fills it, and `accent` decides in what colour. Two different things
 * use that fill — the notes toggle and the Reveal control — and they must not
 * look alike: an accent-less active button keeps the warm teacher-tip palette
 * the notes panel itself uses, so "notes are open" always reads as the same
 * colour wherever it appears, while Reveal passes an accent and gets its own.
 */
export const ChromeButton = ({
  colors,
  type,
  icon,
  label,
  onPress,
  disabled = false,
  active = false,
  accent,
}) => {
  const tone = disabled
    ? colors.textMuted
    : active
    ? accent || colors.teacherTipText
    : colors.textSecondary;

  const activeBorder = accent || colors.teacherTipBorder;
  const activeFill = accent ? `${accent}22` : colors.teacherTipBg;

  return (
    <TouchableOpacity
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ disabled, selected: active }}
      accessibilityLabel={label}
      style={[
        styles.chromeButton,
        {
          borderColor: active ? activeBorder : colors.border,
          backgroundColor: active ? activeFill : "transparent",
          opacity: disabled ? 0.4 : 1,
        },
      ]}
    >
      <MaterialCommunityIcons name={icon} size={18} color={tone} />
      {label ? (
        <Text style={[styles.chromeButtonText, type.chrome, { color: tone }]}>
          {label}
        </Text>
      ) : null}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  eyebrow: {
    fontWeight: "800",
    letterSpacing: 1.2,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  prompt: { fontWeight: "700" },
  heading: { fontWeight: "800" },
  body: { fontWeight: "400" },

  lane: { width: "100%", maxWidth: 1180, alignSelf: "center" },
  laneWide: { maxWidth: 1600 },

  revealPanel: {
    borderRadius: 16,
    borderWidth: 2,
    padding: 22,
    marginTop: 24,
  },
  revealLabelRow: { flexDirection: "row", alignItems: "center", marginBottom: 10 },
  revealLabel: {
    fontWeight: "800",
    letterSpacing: 1.1,
    textTransform: "uppercase",
    marginLeft: 8,
  },

  revealButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: 999,
    borderWidth: 2,
    marginTop: 22,
  },
  revealButtonText: { fontWeight: "800", marginLeft: 8 },

  chromeButton: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 7,
    gap: 6,
  },
  chromeButtonText: { fontWeight: "700", marginLeft: 6 },
});
